import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, X } from "lucide-react";
import { API } from "@/lib/api";

/**
 * TripPhotoGallery — pulls every photo attached to a booking's chat and
 * renders them as a horizontal strip. Shown on the completed-trip receipt
 * so customers get a keepsake record of where their package landed.
 *
 * Props:
 *   - bookingId: string
 *   - variant: "strip" (default) | "compact"
 */
export default function TripPhotoGallery({ bookingId, variant = "strip" }) {
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!bookingId) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API}/messages/${bookingId}`);
        if (cancelled) return;
        const msgs = Array.isArray(res.data) ? res.data : [];
        const withPhotos = msgs.filter((m) => m.photo_url);
        setPhotos(withPhotos);
      } catch {
        /* silent — no gallery is a fine default */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  if (loading || photos.length === 0) return null;

  return (
    <>
      <div
        className="rounded-xl bg-[#121212] border border-white/5 p-3 space-y-2"
        data-testid="trip-photo-gallery"
      >
        <div className="flex items-center gap-2">
          <Camera className="w-3.5 h-3.5 text-[#7AADFF]" />
          <p className="text-xs font-semibold text-white">
            Delivery photos <span className="text-zinc-500 font-normal">({photos.length})</span>
          </p>
        </div>
        <div
          className="flex gap-2 overflow-x-auto pb-1 scrollbar-none"
          style={{ scrollbarWidth: "none" }}
        >
          {photos.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setLightbox(p)}
              className="flex-shrink-0 relative group rounded-lg overflow-hidden border border-white/10 hover:border-[#0047FF]/50 transition-colors"
              data-testid={`gallery-photo-${p.id}`}
              title={new Date(p.created_at).toLocaleString()}
            >
              <img
                src={p.photo_url}
                alt="Delivery photo"
                loading="lazy"
                className={`${variant === "compact" ? "w-16 h-16" : "w-24 h-24"} object-cover`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 py-0.5 text-[9px] text-white/80 text-right">
                {new Date(p.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
              </span>
            </button>
          ))}
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Close"
            data-testid="gallery-lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-3xl w-full space-y-3 text-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.photo_url}
              alt="Delivery photo"
              className="max-h-[80vh] w-auto mx-auto rounded-lg"
            />
            <p className="text-xs text-zinc-400">
              {new Date(lightbox.created_at).toLocaleString()}
              {lightbox.text && lightbox.text !== "📷 Photo proof" ? ` — ${lightbox.text}` : ""}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
